import { useRef } from "react";

import MemberPermissionsTag from "common/components/MemberPermissionsTag";
import { Button } from "common/components/ui/Button";
import {
  Table,
  TableCell,
  TableHead,
  TableRow,
} from "common/components/ui/TableV2";
import { Tag } from "common/components/ui/Tag";
import WorkspaceImage from "common/components/WorkspaceImage";
import useInvitationMutations from "common/datahooks/useInvitationMutations";
import type { Invitation } from "common/types";

function InvitationsTableRow({ invitation }: { invitation: Invitation }) {
  const isAcceptRef = useRef<boolean>();

  const {
    token,
    role,
    permissions,
    workspace_name,
    workspace_id,
    workspace_icon_url,
  } = invitation;

  const { respondToInvitation, isRespondingToInvitation } =
    useInvitationMutations();

  function acceptInvite() {
    isAcceptRef.current = true;
    respondToInvitation({ accept: true, memberToken: token });
  }

  function declineInvite() {
    isAcceptRef.current = false;
    respondToInvitation({ accept: false, memberToken: token });
  }

  return (
    <TableRow key={token}>
      <TableCell>
        <div className="flex gap-x-2.5 items-center">
          <WorkspaceImage id={workspace_id} imageSrc={workspace_icon_url} />
          <div className="flex flex-col gap-y-1">
            <span className="text-md-bold">{workspace_name}</span>
            <span className="text-xs text-black-500">
              Invited by <span className="text-button-xs">test</span>
            </span>
          </div>
        </div>
      </TableCell>

      <TableCell>
        <MemberPermissionsTag role={role} permissions={permissions} />
      </TableCell>

      <TableCell>
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="secondary-black"
            size="sm"
            onClick={declineInvite}
            isLoading={
              isAcceptRef.current === false && isRespondingToInvitation
            }
            disabled={isAcceptRef.current === true && isRespondingToInvitation}
          >
            Decline
          </Button>
          <Button
            variant="secondary-purple"
            size="sm"
            onClick={acceptInvite}
            isLoading={isAcceptRef.current === true && isRespondingToInvitation}
            disabled={isAcceptRef.current === false && isRespondingToInvitation}
          >
            Accept
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export default function InvitationsTable({
  invitations,
}: {
  invitations: Invitation[];
}) {
  return (
    <Table>
      <thead>
        <tr>
          <TableHead>
            Invitations{" "}
            <Tag size="sm" className="ml-1" variant="tertiary">
              {invitations.length}
            </Tag>
          </TableHead>
          <TableHead>Role</TableHead>
          <TableHead />
        </tr>
      </thead>

      <tbody>
        {invitations.map((invitation) => (
          <InvitationsTableRow key={invitation.token} invitation={invitation} />
        ))}
      </tbody>
    </Table>
  );
}
